import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, Hash, ImagePlus } from "lucide-react";
import { useStrapiImageUpload } from "../../hooks/useStrapiImageUpload";
import {
  validateLearningWallTextFields,
  PROFANITY_ERROR_MESSAGE
} from "./profanityValidation";

// ─── Exported types ───────────────────────────────────────────────────────────

export type NoteColor = "yellow" | "blue" | "green" | "pink" | "purple";
export type ContentType = "event" | "course" | "learningpath" | "assessment";

export interface ShareNotePayload {
  colourCode: NoteColor;
  title?: string;
  description: string;
  contentType: ContentType;
  tags: string[];
  images?: String[];
  contentId?: string | null;
}

export interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  onPublish: (payload: ShareNotePayload) => void;
  /** Pre-fill the title field */
  defaultTitle?: string;
  /** Pre-fill the content editor */
  defaultContent?: string;
  /** Pre-fill the tags field (space-separated, e.g. "#learning #growth") */
  defaultTags?: string;
  /** Set the initial note color */
  defaultColor?: NoteColor;
  /** Set the initial content type */
  defaultType?: ContentType;
  /** Pre-load an image URL */
  defaultImage?: string;
  /** Pre-load a Strapi asset id for the thumbnail */
  defaultImageAssetId?: string;
  /** Label for the author — defaults to "You" */
  authorName?: string;
  /** Role label for the author */
  authorRole?: string;
  refetchAllNotes?: () => void;
  fromLearningWall?: boolean;
}

// ─── Internal constants ───────────────────────────────────────────────────────

interface Pal {
  bg: string;
  fold: string;
  text: string;
  dim: string;
  chip: string;
}

const PAL: Record<NoteColor, Pal> = {
  yellow: {
    bg: "#FFE566",
    fold: "#DEC93B",
    text: "#4A3200",
    dim: "rgba(74,50,0,0.48)",
    chip: "rgba(74,50,0,0.1)"
  },
  pink: {
    bg: "#F4A58A",
    fold: "#D98A6E",
    text: "#4A1608",
    dim: "rgba(74,22,8,0.48)",
    chip: "rgba(74,22,8,0.1)"
  },
  green: {
    bg: "#B4EDCA",
    fold: "#83D1A2",
    text: "#0C3A1E",
    dim: "rgba(12,58,30,0.48)",
    chip: "rgba(12,58,30,0.1)"
  },
  blue: {
    bg: "#B4D0FF",
    fold: "#82ADEE",
    text: "#08205C",
    dim: "rgba(8,32,92,0.48)",
    chip: "rgba(8,32,92,0.1)"
  },
  purple: {
    bg: "#D4BBFF",
    fold: "#B296E8",
    text: "#280A5C",
    dim: "rgba(40,10,92,0.48)",
    chip: "rgba(40,10,92,0.1)"
  }
};

const COLOR_ORDER: NoteColor[] = ["yellow", "pink", "green", "blue", "purple"];
const FOLD = 34;
const noteClip = (f: number) =>
  `polygon(0 0, calc(100% - ${f}px) 0, 100% ${f}px, 100% 100%, 0 100%)`;

// ─── Component ────────────────────────────────────────────────────────────────

export function ShareModal({
  open,
  onClose,
  onPublish,
  defaultTitle = "",
  defaultContent = "",
  defaultTags = "",
  defaultColor = "yellow",
  defaultType = "course",
  defaultImage,
  refetchAllNotes,
  fromLearningWall = false
}: ShareModalProps) {
  const [color, setColor] = useState<NoteColor>(defaultColor);
  const [title, setTitle] = useState(defaultTitle);
  const [type, setType] = useState<ContentType>(defaultType);
  const [tags, setTags] = useState(defaultTags);
  const [previewText, setPreviewText] = useState(defaultContent);
  const [imageUrl, setImageUrl] = useState<string[]>(
    defaultImage ? [defaultImage] : []
  );
  const [assetId, setAssetId] = useState<string[]>(
    defaultImage ? [defaultImage] : []
  );
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const p = PAL[color];
  const [isLoading, setIsLoading] = useState(false);
  const { uploadImageToStrapi } = useStrapiImageUpload();
  const [profanityError, setProfanityError] = useState<string | null>(null);

  // Re-initialize from defaults whenever the modal opens
  useEffect(() => {
    if (!open) return;
    setColor(COLOR_ORDER[Math.floor(Math.random() * COLOR_ORDER.length)]);
    setTitle(defaultTitle);
    setTags(defaultTags);
    setType(defaultType);
    setImageUrl(defaultImage ? [defaultImage] : []);
    setAssetId(defaultImage ? [defaultImage] : []);
    setPreviewText(defaultContent);
    // contentEditable needs an imperative update
    requestAnimationFrame(() => {
      if (editorRef.current) editorRef.current.innerText = defaultContent;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reset = () => {
    setColor(COLOR_ORDER[Math.floor(Math.random() * COLOR_ORDER.length)]);
    setTitle("");
    setTags("");
    setPreviewText("");
    setImageUrl([]);
    setAssetId([]);
    setProfanityError(null);
    setType("course");
    if (editorRef.current) editorRef.current.innerHTML = "";
  };

  const close = () => {
    onClose();
    setTimeout(reset, 300);
  };

  const publish = () => {
    const tagList = tags?.split(/\s+/).filter((t) => t.length > 1);
    const text = editorRef.current?.innerText?.trim() ?? "";
    const currentValidation = validateLearningWallTextFields({
      title,
      description: text,
      tags
    });

    if (!text) return;
    if (currentValidation.hasProfanity) {
      setProfanityError(PROFANITY_ERROR_MESSAGE);
      return;
    }
    setProfanityError(null);

    onPublish({
      colourCode: color,
      title: title.trim() || undefined,
      description: text,
      contentType: type,
      tags: tagList.length ? tagList : [],
      images: assetId.length ? assetId : [],
      contentId: ""
    });
    if (refetchAllNotes) refetchAllNotes();
    close();
  };

  const handleImageFile = (file: File) => {
    if (!file.type.startsWith("image/")) return;
    if (uploadImageToStrapi) {
      setIsLoading(true);
      uploadImageToStrapi(file).then((data) => {
        setImageUrl([data.url]);
        setAssetId([data.assetId]);
      });
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleImageFile(file);
  };

  const canPublish =
    previewText.trim().length > 0 &&
    title.trim().length > 0 &&
    tags?.length > 0;

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && close()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/30 backdrop-blur-[8px]" />
        <Dialog.Content
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          aria-describedby={undefined}
        >
          <Dialog.Title className="sr-only">
            Share your learning experience
          </Dialog.Title>
          <AnimatePresence mode="wait">
            <motion.div
              key={`modal-${open}`}
              className="relative w-full"
              style={{ maxWidth: 540 }}
              initial={{ opacity: 0, scale: 0.93, y: 18 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.93, y: 18 }}
              transition={{ type: "spring", stiffness: 320, damping: 28 }}
            >
              {/* Note body */}
              <div
                style={{
                  backgroundColor: p.bg,
                  clipPath: noteClip(FOLD),
                  boxShadow:
                    "0 40px 100px rgba(0,0,0,0.22), 0 8px 28px rgba(0,0,0,0.1)",
                  transition: "background-color 0.22s ease"
                }}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
              >
                <div style={{ maxHeight: "88vh", overflowY: "auto" }}>
                  {/* Header: swatches + close */}
                  <div
                    className="flex items-center justify-between"
                    style={{
                      padding: `16px 16px 0`,
                      paddingRight: `${FOLD + 10}px`
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {COLOR_ORDER.map((c) => {
                        const cp = PAL[c];
                        const active = color === c;
                        return (
                          <button
                            key={c}
                            onClick={() => setColor(c)}
                            className="transition-all duration-200"
                            style={{
                              width: active ? 22 : 15,
                              height: active ? 22 : 15,
                              borderRadius: "50%",
                              backgroundColor: cp.bg,
                              border: `2.5px solid ${active ? cp.fold : cp.bg}`,
                              boxShadow: active
                                ? `0 0 0 3px ${cp.fold}55`
                                : "none",
                              flexShrink: 0
                            }}
                            title={c}
                          />
                        );
                      })}
                    </div>
                    <button
                      onClick={close}
                      className="hover:opacity-60 transition-opacity"
                      style={{ color: p.dim }}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Content type */}
                  <div
                    className="flex gap-1.5"
                    style={{ padding: "14px 16px 0" }}
                  >
                    {(
                      [
                        "course",
                        "event",
                        "learningpath",
                        "assessment"
                      ] as ContentType[]
                    ).map((t) => (
                      <button
                        key={t}
                        onClick={() => setType(t)}
                        className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1.5 transition-all"
                        style={{
                          color: p.text,
                          backgroundColor: type === t ? p.chip : "transparent",
                          borderRadius: 4,
                          border: `1.5px solid ${type === t ? p.fold : "transparent"}`,
                          opacity: type === t ? 1 : 0.38,
                          letterSpacing: "0.1em"
                        }}
                        disabled={!(defaultType == t) && !fromLearningWall}
                      >
                        {t === "learningpath"
                          ? "Learning Path"
                          : t === "assessment"
                            ? "Certification"
                            : t}
                      </button>
                    ))}
                  </div>

                  {/* Title */}
                  <div style={{ padding: "16px 16px 0" }}>
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Title"
                      className="w-full bg-transparent border-none outline-none text-[22px] font-semibold"
                      style={{
                        color: p.text,
                        fontFamily: "'Instrument Sans', sans-serif"
                      }}
                    />
                  </div>

                  {/* Hairline */}
                  <div
                    style={{
                      margin: "12px 16px 0",
                      height: 1,
                      backgroundColor: p.fold,
                      opacity: 0.55
                    }}
                  />

                  {/* Content editor */}
                  <div style={{ padding: "12px 16px 0", minHeight: 120 }}>
                    <div
                      ref={editorRef}
                      contentEditable
                      suppressContentEditableWarning
                      className="outline-none text-[14px] leading-relaxed min-h-[100px]"
                      data-placeholder="What did you learn or experience? Share it here..."
                      style={{ color: p.text }}
                      onInput={(e) =>
                        setPreviewText((e.target as HTMLDivElement).innerText)
                      }
                    />
                    <style>{`
                      [contenteditable]:empty:before {
                        content: attr(data-placeholder);
                        opacity: 0.32;
                        pointer-events: none;
                        display: block;
                      }
                    `}</style>
                  </div>

                  {isLoading && (
                    <div className="relative w-1/2 bg-gray-200 animate-pulse h-36 rounded-xl ml-5 mt-4" />
                  )}

                  {/* Inline image */}
                  {imageUrl.length > 0 && (
                    <div
                      style={{
                        margin: "10px 16px 0",
                        position: "relative",
                        display: "inline-block",
                        maxWidth: "100%"
                      }}
                    >
                      <div
                        style={{
                          borderRadius: 6,
                          overflow: "hidden",
                          maxHeight: 140,
                          display: "flex",
                          alignItems: "center"
                        }}
                      >
                        <img
                          src={imageUrl[0]}
                          alt=""
                          style={{
                            maxWidth: "100%",
                            maxHeight: 140,
                            objectFit: "contain",
                            display: "block"
                          }}
                          className={`transition-opacity duration-300${
                            isLoading ? " opacity-0" : " opacity-100"
                          }`}
                          onLoad={() => setIsLoading(false)}
                        />
                      </div>
                      <button
                        onClick={() => setImageUrl([])}
                        className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center transition-colors"
                        style={{
                          backgroundColor: "rgba(0,0,0,0.45)",
                          color: "#fff"
                        }}
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  )}

                  {/* Add image button */}
                  {!imageUrl.length && !isLoading && (
                    <div style={{ padding: "12px 16px 0" }}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleImageFile(f);
                        }}
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-1.5 text-[12px] font-medium transition-opacity hover:opacity-80"
                        style={{ color: p.dim }}
                      >
                        <ImagePlus className="w-3.5 h-3.5" />
                        <span>Add image</span>
                      </button>
                    </div>
                  )}

                  {/* Hairline */}
                  <div
                    style={{
                      margin: "14px 16px 0",
                      height: 1,
                      backgroundColor: p.fold,
                      opacity: 0.55
                    }}
                  />

                  {/* Tags */}
                  <div
                    className="flex items-center gap-2"
                    style={{ padding: "10px 16px 0" }}
                  >
                    <Hash
                      className="w-3 h-3 flex-shrink-0"
                      style={{ color: p.dim }}
                    />
                    <input
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      placeholder="Add tags...  #learning  #growth"
                      className="flex-1 bg-transparent border-none outline-none text-[12px]"
                      style={{ color: p.text }}
                    />
                  </div>
                  {profanityError && (
                    <div className="px-4 pt-2">
                      <p className="rounded-lg bg-rose-50 px-3 py-2.5 text-xs font-medium text-rose-700 dark:bg-rose-950/40 dark:text-rose-400">
                        {profanityError}
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div
                    className="flex items-center justify-between"
                    style={{
                      padding: "14px 16px 16px",
                      marginTop: 12,
                      borderTop: `1px solid ${p.fold}`
                    }}
                  >
                    <button
                      onClick={close}
                      className="text-[12px] font-medium transition-opacity hover:opacity-60"
                      style={{ color: p.dim }}
                    >
                      Cancel
                    </button>
                    <div className="flex items-center gap-2">
                      {/* <button
                        className="text-[12px] font-medium px-3 py-1.5 transition-opacity hover:opacity-60"
                        style={{ color: p.dim, borderRadius: 6 }}
                      >
                        Save draft
                      </button> */}
                      <button
                        onClick={publish}
                        className="text-[12px] font-semibold px-4 py-1.5 transition-all active:scale-[0.97]"
                        style={{
                          backgroundColor: p.text,
                          color: p.bg,
                          borderRadius: 6,
                          opacity: canPublish && !isLoading ? 1 : 0.35,
                          cursor:
                            canPublish && !isLoading ? "pointer" : "default"
                        }}
                        disabled={!canPublish || isLoading}
                      >
                        Publish to Canvas ↑
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fold triangle overlay */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: FOLD,
                  height: FOLD,
                  background: `linear-gradient(225deg, ${p.fold} 50%, transparent 50%)`,
                  transition: "background 0.22s ease",
                  pointerEvents: "none"
                }}
              />
            </motion.div>
          </AnimatePresence>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
